import { OFFLINE_GAMES_ENABLED } from "./featureEnabled";

// Talks to the offline service worker (public/sw.js). It's only registered for
// users who downloaded a game, so regular visitors never run a service worker.
const SW_URL = "/sw.js";
const MESSAGE_TIMEOUT_MS = 30000;

export const isOfflinePlaySupported = () =>
	OFFLINE_GAMES_ENABLED &&
	typeof window !== "undefined" &&
	window.isSecureContext &&
	"serviceWorker" in navigator &&
	"caches" in window &&
	"indexedDB" in window;

export const registerOfflineServiceWorker = async () => {
	if (!isOfflinePlaySupported()) throw new Error("Offline play isn't supported in this browser");
	await navigator.serviceWorker.register(SW_URL, { scope: "/" });
	return navigator.serviceWorker.ready;
};

const sendMessage = async (type, payload) => {
	if (!OFFLINE_GAMES_ENABLED) throw new Error("Offline games are disabled");
	const registration = await navigator.serviceWorker.ready;
	const worker = registration.active;
	if (!worker) throw new Error("Offline service worker isn't active");
	return new Promise((resolve, reject) => {
		const channel = new MessageChannel();
		const timer = setTimeout(() => reject(new Error(`${type} timed out`)), MESSAGE_TIMEOUT_MS);
		channel.port1.onmessage = ({ data }) => {
			clearTimeout(timer);
			if (data?.ok) resolve(data.result);
			else reject(new Error(data?.error || `${type} failed`));
		};
		worker.postMessage({ type, payload }, [channel.port2]);
	});
};

const currentUserToken = () => localStorage.getItem("token") || "";

// Sends progress saved offline to the server. Passing the signed-in user's
// current token lets progress saved under an expired one sync too.
export const syncOfflineProgress = () => sendMessage("akedo-sync", { userToken: currentUserToken() });

// Caches the current site build so it opens offline after a deploy.
export const refreshOfflineSite = () => sendMessage("akedo-refresh-shell");

// Loads the player's server progress into the offline copy of a game.
export const bootstrapOfflineProgress = (appId, userToken) => sendMessage("akedo-bootstrap-state", { appId, userToken });

// After playing a downloaded game online: copy that progress to the offline copy too.
export const refreshOfflineProgress = (appId) => bootstrapOfflineProgress(appId, currentUserToken());

// Deletes a game's files; its progress is kept.
export const removeOfflineGameFiles = (appId) => sendMessage("akedo-remove-game", { appId });

// Ids of the signed-in user's games with progress waiting to sync.
export const pendingOfflineProgress = async () => (await sendMessage("akedo-pending", { userToken: currentUserToken() }))?.appIds || [];

// Is this page served through the worker? (A hard reload bypasses it.)
export const isControlledByOfflineWorker = () => OFFLINE_GAMES_ENABLED && Boolean(navigator.serviceWorker?.controller);

// Remove only Akedo's offline worker and shell cache. Keep downloaded game
// files and saved progress so testing can resume without losing player data.
export const retireOfflineServiceWorker = async () => {
 if (OFFLINE_GAMES_ENABLED || !navigator.serviceWorker) return;
 const registrations = await navigator.serviceWorker.getRegistrations();
 await Promise.all(registrations.filter(registration =>
  [registration.active, registration.waiting, registration.installing].some(worker =>
   worker && new URL(worker.scriptURL).origin === location.origin && new URL(worker.scriptURL).pathname === SW_URL
  )).map(registration => registration.unregister()));
 if ("caches" in window) {
  const names = await caches.keys();
  await Promise.all(names.filter(name => name.startsWith("akedo-shell-")).map(name => caches.delete(name)));
 }
};
